import React from "react";
import cx from "classnames";
import {
  formatSpellActionType, getSpellDamageType, getSpellLevelProperty,
  ISpellActivation,
  ISpellComponents,
  ISpellData,
  ISpellMaterials,
  ISpellRange,
  ISpellTarget,
} from "models/Spell"
import {
  ConcentrationIcon,
  ConeIcon,
  ConsumeMaterialIcon,
  CreatureIcon,
  CubeIcon,
  CylinderIcon,
  LineIcon,
  ObjectIcon,
  RadiusIcon,
  RitualIcon,
  SphereIcon,
  SquareIcon,
  WallIcon,
} from "components/ui/icons/SpellIcons";
import { ClassIcon } from "components/ui/icons/ClassIcons";
import styles from "components/ui/spell/SpellComponents.module.scss";
import Chip, { IconChip } from "components/ui/chip/Chip";
import IconQuadrant from "components/ui/icons/IconQuadrant";
import { capitaliseEachWordCase } from "utils/functions";
import ComplexRune from "components/ui/icons/ComplexRune";
import Tooltip from "components/ui/tooltip/Tooltip";

export const SpellSpecialCharacteristics = ({
  components,
  condensed = true,
  className,
}: {
  components: any;
  condensed?: boolean;
  className?: string;
}) => {
  return condensed ? (
    <>
      {components.concentration && (
        <Tooltip content={"Concentration"} arrow={false}>
          <ConcentrationIcon className={styles.conc} />
        </Tooltip>
      )}
      {components.ritual && (
        <Tooltip content={"Ritual"}>
          <RitualIcon className={styles.ritual} />
        </Tooltip>
      )}
    </>
  ) : (
    <div className={cx(styles.horizontal, className)}>
      {components.concentration && (
        <Chip theme="primary" icon={<ConcentrationIcon />} condensed>
          Concentration
        </Chip>
      )}
      {components.ritual && (
        <Chip
          theme="secondary"
          icon={<RitualIcon className={styles.ritual} />}
          condensed
        >
          Ritual
        </Chip>
      )}
    </div>
  );
};

export const SpellComponents = ({
  components,
  materials,
  condensed = true,
  className,
}: {
  components: ISpellComponents;
  materials: ISpellMaterials;
  condensed?: boolean;
  className?: string;
}) => {
  return (
    <div
      className={cx(
        styles.horizontal,
        className,
        !condensed &&
          materials.value &&
          materials.value !== "" &&
          styles.multiLine
      )}
    >
      {!condensed && <span className={styles.label}>Components: </span>}
      <div className={styles.fluffWrapper}>
        <div className={styles.horizontal}>
          {components.vocal && <IconChip variant="lightFilled">V</IconChip>}
          {components.somatic && <IconChip variant="lightFilled">S</IconChip>}
          {components.material && <IconChip variant="lightFilled">M</IconChip>}
          {materials?.consumed && (
            <Chip
              icon={<ConsumeMaterialIcon />}
              iconVariant="lightFilled"
              theme="danger"
              condensed
            >
              {materials.cost && `${materials.cost.toLocaleString("en")}gp`}
            </Chip>
          )}
        </div>
        {!condensed && materials.value && materials.value !== "" && (
          <div className={styles.fluff}>{`(${materials.value})`}</div>
        )}
      </div>

      {/*{components.vocal && <IconChip variant="lightFilled">V</IconChip>}*/}
      {/*{components.somatic && <IconChip variant="lightFilled">S</IconChip>}*/}
      {/*{components.material && <IconChip variant="lightFilled">M</IconChip>}*/}
      {/*{materials?.consumed && (*/}
      {/*  <Chip*/}
      {/*    icon={<ConsumeMaterialIcon />}*/}
      {/*    iconVariant="lightFilled"*/}
      {/*    theme="danger"*/}
      {/*    condensed*/}
      {/*  >*/}
      {/*    {materials.cost && `${materials.cost.toLocaleString("en")}gp`}*/}
      {/*  </Chip>*/}
      {/*)}*/}
      {/*{!condensed && materials.value && materials.value !== "" && (*/}
      {/*  <span className={styles.fluff}>{`(${materials.value})`}</span>*/}
      {/*)}*/}
    </div>
  );
};
export const SpellMaterialsFluff = ({
  materials,
  condensed = true,
  className,
}: {
  materials: ISpellMaterials;
  condensed?: boolean;
  className?: string;
}) => {
  return !condensed && materials.value && materials.value !== "" ? (
    <span className={styles.materialFluff}>{`(${materials.value})`}</span>
  ) : (
    <></>
  );
};
export const SpellType = ({
  actionType,
  save,
  condensed = true,
  className,
}: {
  save?: {
    ability: string;
  };
  actionType: string;
  condensed?: boolean;
  className?: string;
}) => {
  return (
    <div className={cx(styles.horizontal, className)}>
      {!condensed && <span className={styles.label}>Spell Type:</span>}
      {formatSpellActionType(actionType, save)}
    </div>
  );
};

export const SpellMaterials = ({
  materials,
  condensed = true,
  className,
}: {
  materials: ISpellMaterials;
  condensed?: boolean;
  className?: string;
}) => {
  return condensed ? (
    <>
      {materials?.consumed && (
        <Chip
          icon={<ConsumeMaterialIcon />}
          iconVariant="lightFilled"
          theme="danger"
          condensed
        >
          {materials.cost && `${materials.cost.toLocaleString("en")}gp`}
        </Chip>
      )}
    </>
  ) : (
    <div className={cx(styles.horizontal, className)}>
      {materials.value}
      {materials?.consumed && (
        <Chip
          icon={<ConsumeMaterialIcon />}
          iconVariant="lightFilled"
          theme="danger"
          condensed
        >
          {materials.cost && `${materials.cost.toLocaleString("en")}gp`}
        </Chip>
      )}
    </div>
  );
};

export const SpellCasting = ({
  activation,
  condensed = true,
  className,
}: {
  activation: ISpellActivation;
  condensed?: boolean;
  className?: string;
}) => {
  return condensed ? (
    <>
      {activation.cost !== 1 || ["minute", "hour"].includes(activation.type)
        ? `${activation.cost} `
        : ""}
      {capitaliseEachWordCase(
        `${activation.type}${activation.cost > 1 ? "s" : ""}`
      )}
    </>
  ) : (
    <div
      className={cx(
        styles.horizontal,
        className,
        !condensed && activation.condition && styles.multiLine
      )}
    >
      <span className={styles.label}>Casting Time: </span>

      {/*{activation.cost}{" "}*/}
      {/*{capitaliseEachWordCase(*/}
      {/*  `${activation.type}${activation.cost > 1 ? "s" : ""}`*/}
      {/*)}{" "}*/}
      {/*{!condensed && activation.condition && (*/}
      {/*  <div className={styles.fluff}>{`(${activation.condition})`}</div>*/}
      {/*)}*/}

      <div className={styles.fluffWrapper}>
        <div>
          {activation.cost}{" "}
          {capitaliseEachWordCase(
            `${activation.type}${activation.cost > 1 ? "s" : ""}`
          )}{" "}
        </div>
        {!condensed && activation.condition && (
          <div className={styles.fluff}>{`(${activation.condition})`}</div>
        )}
      </div>
    </div>
  );
};

export const SpellRange = ({
  range,
  target,
  condensed = true,
  className,
}: {
  range: ISpellRange;
  target: ISpellTarget;
  condensed?: boolean;
  className?: string;
}) => {
  return condensed ? (
    <>
      {formatRange(range.value, range.units)} <span className={styles.label} />
      {target.value && target.units !== "" ? (
        <Chip
          icon={getRangeType(target.type)}
          iconVariant="lightFilled"
          theme="secondary"
          condensed
        >
          {target.units === "spec"
            ? `${target.value} ${target.type}${+target.value > 1 ? "s" : ""}`
            : `${target.value} ${target.units}`}
        </Chip>
      ) : (
        ""
      )}
    </>
  ) : (
    <div className={cx(styles.horizontal, className)}>
      <span className={styles.label}>Range: </span>
      {formatRange(range.value, range.units)} <span className={styles.label} />
      {target.value && target.units !== "" ? (
        <Chip
          icon={getRangeType(target.type)}
          iconVariant="lightFilled"
          theme="secondary"
          condensed
        >
          {target.units === "spec"
            ? `${target.value} ${target.type}${+target.value > 1 ? "s" : ""}`
            : `${target.value} ${target.units}`}
        </Chip>
      ) : (
        ""
      )}
    </div>
  );
};
export const SpellLevel = ({
  level,
  className,
  large = false,
  children,
}: {
  level: number;
  className?: string;
  large?: boolean;
  children?: React.ReactNode;
}) => (
  <div className={cx(styles.levelRune, className, large && styles.large)}>
    <ComplexRune className={styles.rune} />
    <div
      className={styles.icon}
      style={{
        background: getSpellLevelProperty(level, 'color'),
        color: level === 0 ? "#ffd28b" : "#fff",
      }}
    >
      {children ? children : level === 0 ? "C" : level}
    </div>
  </div>
);

export const SpellDamage = ({
  damage,
  condensed = true,
  className,
}: {
  damage: string[];
  condensed?: boolean;
  className?: string;
}) =>
  condensed ? (
    <>
      {damage.map((d: string) => {
        const dmgType = getSpellDamageType(d,'all') as { color: string; icon: React.ReactNode };
        const { icon, color } = dmgType || defaultDamageType;
        const style = { color: color, background: color + 28 };
        return (
          <Tooltip content={capitaliseEachWordCase(d)} key={d}>
            <IconChip style={style}>{icon}</IconChip>
          </Tooltip>
        );
      })}
    </>
  ) : (
    <div className={cx(styles.horizontal, className)}>
      {damage.length > 0 && (
        <span className={styles.label}>Damage Types: </span>
      )}
      {damage.map((d: string) => {
        const dmgType = getSpellDamageType(d,'all') as { color: string; icon: React.ReactNode };
        const { icon, color } = dmgType || defaultDamageType;
        const style = { color: color, background: color + 28 };
        return (
          <Chip icon={icon} key={d} style={style} iconStyle={style} condensed>
            {capitaliseEachWordCase(d)}
          </Chip>
        );
      })}
    </div>
  );

export const SpellDuration = ({
  data,
  className,
}: {
  data: ISpellData;
  className?: string;
}) => {
  const { duration, components } = data;
  return (
    <div
      className={cx(
        styles.horizontal,
        className,
        components.concentration && styles.full
      )}
    >
      <span className={styles.label}>Duration: </span>
      {components.concentration && (
        <>
          <span>
            <Chip theme="primary" icon={<ConcentrationIcon />} condensed>
              Concentration
            </Chip>
          </span>
          &nbsp;up to&nbsp;
        </>
      )}
      {formatDuration(duration.value, duration.units)}
    </div>
  );
};

export const SpellClasses = ({
  classes,
  className,
  label = "Classes",
  theme = "primary",
}: {
  classes: string[];
  className?: string;
  label?: string;
  theme?: ThemeVariants;
}) => {
  return (
    <div className={cx(styles.horizontal, styles.multiLine, className)}>
      <b className={styles.label}>{label}: </b>
      <div className={cx(styles.horizontal, styles.wrap)}>
        {classes &&
          classes.map((c) => {
            return (
              <Chip
                key={c}
                className={styles.stackedChips}
                icon={<ClassIcon classIconName={c.split("::")[0]} />}
                theme={theme}
                condensed
              >
                {c.replace("::", " - ")}
              </Chip>
            );
          })}
      </div>
    </div>
  );
};

const formatRange = (value: number | null, units: string | null) => {
  if (units === "touch" || units === "self") {
    return capitaliseEachWordCase(units);
  }
  return `${value && `${value} `}${units}`;
};

const formatDuration = (value: number | null, units: string) => {
  const suffix = value && value > 1 ? "s" : "";
  switch (units) {
    case "inst":
      return "Instantaneous";
    case "min":
      return `${value} ${units}${suffix}`;
    default:
      return value ? `${value} ${units}${suffix}` : units;
  }
};

const getRangeType = (type: string) => {
  switch (type) {
    case "cone":
      return <ConeIcon />;
    case "sphere":
      return <SphereIcon />;
    case "radius":
      return <RadiusIcon />;
    case "square":
      return <SquareIcon />;
    case "cylinder":
      return <CylinderIcon />;
    case "cube":
      return <CubeIcon />;
    case "line":
      return <LineIcon />;
    case "wall":
      return <WallIcon />;
    case "space":
      return <SphereIcon />;
    case "creature":
      return <CreatureIcon />;
    case "object":
      return <ObjectIcon />;
    default:
      return <IconQuadrant />;
  }
};

const defaultDamageType = { color: "#736ca6", icon: "" };
